/**
 * The inline duplicate check of docs/08-frontend-and-ux.md §3.5.
 *
 * A family tree is exactly the kind of data where duplicates are easy to create
 * and expensive to undo. Names repeat across generations by tradition — three
 * Mohammeds and two Fatimas in one branch is ordinary — and the person keying
 * them in has no way to know that great-uncle Hassan was entered last Tuesday by
 * someone else. Once the duplicate exists, merging it means retiring one row,
 * re-pointing every relation that hangs off it, and (outside bootstrap) putting
 * each of those corrections to a vote.
 *
 * So the check runs *while they type*, against the same unaccented trigram
 * search the people list uses, narrowed by a birth-year window when a year is
 * known. It is deliberately **never blocking**: the council is the real gate, a
 * cousin genuinely may share a grandfather's name, and a warning that refuses to
 * let you continue teaches people to work around it.
 */

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { personsApi } from '../../api/client.js';
import { lifespan, personName } from './labels.js';
import ui from '../../ui/components.module.css';
import styles from './people.module.css';

export interface DuplicateWarningProps {
  givenNames: string;
  familyName: string;
  birthYear: number | null;
  /** In edit mode the person is not their own duplicate. */
  excludeId?: string;
}

/** Years either side of a stated birth year that still count as "might be them". */
const YEAR_WINDOW = 3;

export function DuplicateWarning({
  givenNames,
  familyName,
  birthYear,
  excludeId,
}: DuplicateWarningProps): JSX.Element | null {
  const { t } = useTranslation();
  const [term, setTerm] = useState('');
  const [dismissed, setDismissed] = useState(false);

  const needle = [givenNames.trim(), familyName.trim()].filter(Boolean).join(' ');

  useEffect(() => {
    const timer = setTimeout(() => setTerm(needle), 400);
    return () => clearTimeout(timer);
  }, [needle]);

  // Two characters is not a name; searching on one would match the whole tree
  // and teach the user to ignore this panel.
  const enabled = term.length >= 3;

  const matches = useQuery({
    queryKey: ['duplicate-check', term, birthYear],
    queryFn: () =>
      personsApi.list({
        q: term,
        limit: 5,
        ...(birthYear != null
          ? { birth_year_min: birthYear - YEAR_WINDOW, birth_year_max: birthYear + YEAR_WINDOW }
          : {}),
      }),
    enabled,
  });

  if (!enabled || dismissed) return null;
  const found = (matches.data?.data ?? []).filter((person) => person.id !== excludeId);
  if (found.length === 0) return null;

  return (
    <div className={ui.notice} role="status">
      <p style={{ marginBlockStart: 0 }}>
        <strong>{t('people.duplicateTitle', { count: found.length })}</strong>{' '}
        {t('people.duplicateHelp')}
      </p>
      <ul className={styles.list}>
        {found.map((person) => (
          <li key={person.id}>
            {/* A new tab, always: the half-filled form behind this panel is work
                nobody should lose to answer "is this her?". */}
            <Link to={`/people/${person.id}`} target="_blank" rel="noreferrer">
              {personName(person)}
            </Link>
            {lifespan(person) ? <span className="tabular"> {lifespan(person)}</span> : null}
            {person.status === 'pending' && (
              <span className={styles.badgePending}>{t('enums.assertion_status.pending')}</span>
            )}
          </li>
        ))}
      </ul>
      <button type="button" className={ui.buttonSecondary} onClick={() => setDismissed(true)}>
        {t('people.duplicateDismiss')}
      </button>
    </div>
  );
}
